import { DisputeIcon, MoneyBackIcon } from "@xola/icons";
import { Alert } from "@xola/ui-kit";
import { getPaymentLineItems } from "common";
import { get, isNumber } from "lodash";
import React from "react";
import { Audit, LineItemDisputeStatus, Purchase, PurchaseItem } from "types";
import { Currency } from "../../../Common/Currency";
import { TimelineItem } from "../TimelineItem";

interface DisputeTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const DisputeTimelineItem = ({ purchase, item, audit, showDate = false }: DisputeTimelineItemProps) => {
    if (!DisputeTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    const copy = getCopy(audit, purchase);
    const isDisputeWon = getStatus(audit) === LineItemDisputeStatus.WON;

    return (
        <TimelineItem
            title={copy.title}
            audit={audit}
            icon={isDisputeWon ? <MoneyBackIcon /> : <DisputeIcon />}
            color={isDisputeWon ? "success" : "danger"}
            showDate={showDate}
        >
            <div>
                <Currency>{Math.abs(getAmount(audit))}</Currency>

                <Alert color="secondary">{copy.message}</Alert>
            </div>
        </TimelineItem>
    );
};

DisputeTimelineItem.supportsAudit = (purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    const lineItems = Object.values(get(audit, "data.lineItems", {}));
    return (
        lineItems.some((lineItem) => get(lineItem, "payment.dispute")) &&
        getStatus(audit) !== LineItemDisputeStatus.LOST
    );
};

const getAmount = (audit: Audit) => {
    const [lineItem] = Object.values(get(audit, "data.lineItems", {})) as any;
    const { status = null, amountReinstated = null, amountWithdrawn = null } = lineItem.payment?.dispute?.new ?? {};

    if (status === LineItemDisputeStatus.WON && amountReinstated) {
        return amountReinstated;
    }

    if (status === LineItemDisputeStatus.LOST && amountWithdrawn) {
        return amountWithdrawn;
    }

    return get(lineItem, "payment.dispute.new.amount") ?? null;
};

const getStatus = (audit: Audit): LineItemDisputeStatus => {
    const [lineItem] = Object.values(get(audit, "data.lineItems", {}));
    return get(lineItem, "payment.dispute.new.status");
};

const getCopy = (audit: Audit, purchase: Purchase) => {
    const [lineItem] = Object.values(get(audit, "data.lineItems", {})) as any;
    const paymentLineItem = getPaymentLineItems(purchase).find((paymentLineItem) => paymentLineItem.id === lineItem.id);
    const last4 = paymentLineItem?.payment?.meta?.cardSummary?.last4;
    const amountWithdrawn = isNumber(lineItem?.payment?.dispute?.new?.chargebackFee);
    const isFullAmountReinstated = paymentLineItem?.payment?.dispute?.amount === getAmount(audit);
    const fundsReinstatedMessage = {
        title: "Funds Reinstated",
        message: isFullAmountReinstated
            ? "The issuing bank has resolved the dispute in your favor and the disputed amount has been returned to you."
            : "The disputed amount has been partially returned to you.",
    };

    return getStatus(audit) === LineItemDisputeStatus.WON
        ? fundsReinstatedMessage
        : {
              title: amountWithdrawn ? "Funds Withdrawn" : "Charge disputed",
              message: last4
                  ? `Your customer initiated a credit card dispute with their bank for the charge on the card ending in ****${last4}.`
                  : "Your customer initiated a credit card dispute with their bank for the charge.",
          };
};
